import React, { useState } from "react";
import $ from "clients/RawClient";
import { addSnackBar } from "actions/snack-bar-actions";
import { useAppDispatch } from "../../../../../util/store-types";
import type { ShortlistFormProps } from "./types";
import HeapHelpers from "helpers/heap-helpers";

const ShortlistForm = (props: ShortlistFormProps) => {
  const {
    fetchShortlists,
    hideShortlistCreateForm,
    product,
    productId,
    toggleForm,
    isMobileViewport,
    isFavoritesPage = false,
  } = props;

  const [shortlistName, setShortlistName] = useState("");
  const [invalidName, setInvalidName] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const dispatch = useAppDispatch();

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (submitting) return;

    if (shortlistName.trim().length === 0) {
      setInvalidName(true);
      return;
    }

    setSubmitting(true);

    const postData: {
      options: { dateFormat: string };
      name: string;
      styleNames?: string[];
    } = {
      options: {
        dateFormat: "YYYY-MM-DD",
      },
      name: shortlistName,
    };

    if (productId) {
      postData.styleNames = [productId];
    }

    $.ajax({
      type: "POST",
      url: "/shortlist",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
      data: JSON.stringify(postData),
    }).then(
      (data: { name: string; id: string; eventDate: string }) => {
        fetchShortlists?.();
        toggleForm?.();

        if (productId && product) {
          const productName = (product.displayName as string) || "this item";
          dispatch(
            addSnackBar({
              copyPrimary: (
                <span>
                  We added {productName} to {data.name}.{" "}
                  <a href={`/shortlist/${data.id}`} className="underline">
                    View list.
                  </a>
                </span>
              ),

              duration: 5000,
            })
          );
        } else {
          dispatch(
            addSnackBar({
              copyPrimary: `Your list "${data.name}" has been created.`,
              duration: 3000,
            })
          );
        }
        setSubmitting(false);
        const heapEvent = isFavoritesPage ? "sf_create_favorites_shortlist" : "sf_hearts_create_shortlist";
        HeapHelpers.fireHeapEvent(heapEvent, { shortlistName: data.name, shortlistId: data.id });
      },
      function () {
        setSubmitting(false);
      }
    );
  };

  const handleClose = () => {
    const heapEvent = isFavoritesPage ? "sf_favorites_create_cancel_shortlist" : "sf_hearts_create_cancel_shortlist";
    HeapHelpers.fireHeapEvent(heapEvent, {});
    hideShortlistCreateForm();
  };

  const onShortlistNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShortlistName(event.target.value);
    setCharCount(event.target.value.length);
    if (invalidName) {
      setInvalidName(false);
    }
  };

  const paddingTopClass = isMobileViewport ? "pt-[17px]" : "pt-[13px]";
  return (
    <form onSubmit={onSubmit} method="post" className="absolute inset-0 flex flex-col justify-between">
      <div>
        <div className={`w-full flex items-center justify-center px-6 ${paddingTopClass}`}>
          <h4>Create List</h4>
        </div>
        <hr className="w-full border-lighterGray my-4" />
      </div>
      <div className="px-6 flex flex-col justify-center items-center">
        <h4 className="block font-sans text-xl font-light leading-none text-gray-800 mb-2">Give your list a name</h4>
        <p className="text-md text-gray-500 mb-4">Use any letters, numbers, symbols, or emojis.</p>
        <input
          id="shortlist_name"
          maxLength={64}
          name="shortlist[name]"
          placeholder="List Name*"
          type="text"
          onChange={onShortlistNameChange}
          value={shortlistName}
          className="w-full p-3 border border-lighterGray rounded-md text-darkerGray font-sans text-sm placeholder:text-darkerGray"
        />
        <div className="w-full flex justify-between mt-1 text-sm">
          {charCount === 64 ? (
            <p className="text-darkerGray" data-test-id="char-limit-error">
              Character limit reached
            </p>
          ) : (
            <div />
          )}
          <p className="text-darkerGray">{charCount}/64</p>
        </div>
      </div>
      <div>
        <hr className="w-full border-lighterGray my-4" />
        <div className="px-4 pb-6 flex gap-4">
          <button
            onClick={handleClose}
            type="button"
            className="flex-1 px-6 py-3 border border-wine rounded-full text-wine font-medium text-base hover:bg-gray-100 transition-colors duration-200">
            Cancel
          </button>
          <button
            data-style-name={productId}
            data-shortlist-name={shortlistName}
            type="submit"
            disabled={!shortlistName.trim() || submitting}
            className="flex-1 px-6 py-3 bg-wine text-white rounded-full font-medium text-base hover:bg-gray-800 transition-colors duration-200 disabled:bg-darkerGray disabled:cursor-not-allowed">
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default ShortlistForm;
